import { NgModule, LOCALE_ID, CUSTOM_ELEMENTS_SCHEMA, inject, provideAppInitializer } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';

import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { ThesauriComponent } from '@thesauri/thesauri.component';
import { ThesaurusDetailComponent } from '@thesaurus-detail/thesaurus-detail.component';
import { ThesaurusIndexComponent } from '@thesaurus-index/thesaurus-index.component';
import { ThesaurusTermsComponent } from '@thesaurus-terms/thesaurus-terms.component';
import { ConfigComponent } from '@maintain/config.component';
import { ServiceErrorComponent } from '@service-error/service-error.component';
import { ModalComponent } from '@modal/modal.component';
import { CollapsingNotesComponent } from '@collapsing-notes/collapsing-notes.component';
import { CollapsingQualifierNotesComponent } from '@collapsing-qualifier-notes/collapsing-qualifier-notes.component';
import { HeaderLogoComponent } from '@header-logo/header-logo.component';
import { SelectDropdownComponent } from '@select-dropdown/select-dropdown.component';
import { PopoverComponent } from '@popover/popover.component';

import { I18NEXT_SERVICE, I18NextLoadResult, I18NextModule, ITranslationService } from 'angular-i18next';
import HttpApi from 'i18next-browser-languagedetector';
import LanguageDetector from 'i18next-http-backend';
import { FormsModule } from '@angular/forms';
import { NgxGoogleAnalyticsModule, NgxGoogleAnalyticsRouterModule } from 'ngx-google-analytics';
import {MatTreeModule} from '@angular/material/tree';
import { ThesaurusSingleTermComponent } from './components/thesaurus-single-term/thesaurus-single-term.component';
import { ThesaurusHierarchicalViewComponent } from './components/thesaurus-hierarchical-view/thesaurus-hierarchical-view.component';
import { ThesaurusEntryComponent } from '@thesaurus-entry/thesaurus-entry.component';
import lookup from 'socket.io-client';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { ThesaurusFooterComponent } from '@thesaurus-footer/thesaurus-footer.component';
import { ThesaurusRootComponent } from '@thesaurus-root/thesaurus-root.component';
import { StatusComponent } from './components/status/status.component';
import { AddTermBannerComponent } from './components/add-term-banner/add-term-banner.component';
import { NgxSpinnerModule } from 'ngx-spinner';
import { IconLegendComponent } from '@icon-legend/icon-legend.component';
import { AppConfigService } from './services/config/app-config.service';

const i18nextOptions = {
    supportedLngs: ['ar', 'cs', 'de',  'en', 'es', 'fr', 'hu', 'in', 'it', 'ja', 'ko', 'no', 'pl', 'pl-PT', 'pt', 'ru', 'th', 'tr', 'zh-CN', 'zh-TW'],
    fallbackLng: 'en',
    //nonExplicitSupportedLngs: false, //TODO: decide if this is needed
    debug: false,
    returnEmptyString: false,
    returnObjects: true,
    ns: ['translations'],
    interpolation: {
        format: I18NextModule.interpolationFormat(),
        defaultVariables: {
            space: '\u00A0'
        }
    },
    // backend plugin options
    backend: {
        loadPath: 'assets/locales/{{lng}}.{{ns}}.json'
    },
    // lang detection plugin options
    detection: {
        // order and from where user language should be detected
        order: ['querystring', 'navigator'],
        lookupQuerystring: 'amp;lng',
        caches: [] //empty to override i18next default caches
    }
};

export function appInit(i18next: ITranslationService) {
    return () => {
        const promise: Promise<I18NextLoadResult> = 
            i18next
            .use(HttpApi)
            .use(LanguageDetector)
            .init(i18nextOptions);
        return promise;
    };
}

export function localeIdFactory(i18next: ITranslationService) {
    return i18next.language;
}

export function appConfigInit(appConfigService: AppConfigService) {
  return () => {
    return appConfigService.loadConfig();
  };
}

export const I18N_PROVIDERS = [
    provideAppInitializer(() => {
        const initializerFn = (appInit)(inject(I18NEXT_SERVICE));
        return initializerFn();
      }),
  provideAppInitializer(() => {
    const initializerFn = (appConfigInit)(inject(AppConfigService));
    return initializerFn();
    }),
    {
        provide: LOCALE_ID,
        deps: [I18NEXT_SERVICE],
        useFactory: localeIdFactory
    }
];

export function getEnvironment() {
    const hostname = window.location['hostname'].toString();
    if (hostname.indexOf('localhost') !== -1|| hostname.indexOf('.dev') !== -1) {
        return '-dev';
    } else if(hostname.startsWith('pre')) { 
        return '-preprod';
    }
    return '';
}

export function getClosePopup(closePopup: any)  {
    if (closePopup === undefined || closePopup === null) {
      closePopup = true; //Default to true if not specified
    } else if (closePopup === 'false') {
      closePopup = false; //Convert string 'false' to boolean false
    } else {
      closePopup = true; //Convert any other value to true
    }
    return closePopup;
}

export function getLookup(lookup: any) {
    if (lookup === undefined || lookup === null || lookup === 'false') {
      lookup = false;
    } else if (lookup === 'true') {
      lookup = true;
    }
    return lookup;
}

export function getAppendToField(appendField: any) {
    if (appendField === undefined || appendField === null) {
      appendField = false; //Default to true if not specified
    } else if (appendField === 'false') {
      appendField = false; //Convert string 'false' to boolean false
    } else {
      appendField = true; //Convert any other value to true
    }
    return appendField;
}

export function getProxyRedirect(proxyRedirect: any) {
    if (proxyRedirect === undefined || proxyRedirect === null) {
      proxyRedirect = true; //Default to true if not specified
    } else if (proxyRedirect === 'false') {
      proxyRedirect = false; //Convert string 'false' to boolean false
    } else {
      proxyRedirect = true; //Convert any other value to true
    }
    return proxyRedirect;
}

export function getHelpPageUrl() {
    return `https://www.proquest.com/help/academic/Thesauri.html`;
}

@NgModule({ declarations: [
        AppComponent,
        ConfigComponent,
        ThesauriComponent,
        ThesaurusDetailComponent,
        ThesaurusIndexComponent,
        ServiceErrorComponent,
        ThesaurusTermsComponent,
        ThesaurusSingleTermComponent,
        ThesaurusHierarchicalViewComponent,
        ThesaurusEntryComponent,
        ModalComponent,
        CollapsingNotesComponent,
        CollapsingQualifierNotesComponent,
        ThesaurusFooterComponent,
        ThesaurusRootComponent,
        StatusComponent,
        HeaderLogoComponent,
        SelectDropdownComponent,
        AddTermBannerComponent,
        PopoverComponent,
        IconLegendComponent
    ],
    bootstrap: [AppComponent], 
    imports: [
        NgxGoogleAnalyticsModule.forRoot('G-WJ3VC92T6F'),
        NgxGoogleAnalyticsRouterModule,
        I18NextModule.forRoot(),
        FormsModule,
        MatTreeModule,
        BrowserModule,
        AppRoutingModule,
        NgxSpinnerModule,
        BrowserAnimationsModule
    ], 
    providers: [
        provideHttpClient(withInterceptorsFromDi()),
        I18N_PROVIDERS,
        provideAnimationsAsync('noop')
    ],
    schemas: [
        CUSTOM_ELEMENTS_SCHEMA
    ]
})

export class AppModule { }
